import {
  escapePlaintextHtml,
  sanitizeRichHtml,
} from "./chatFrontendGuards.js?v=1.0.2";
import {
  humanizeReviewSummary,
  humanizeReviewWarnings,
} from "./reviewUserCopy.js?v=1.0.0";
import {
  createToolResultAccessError,
  toolResultAccessFeedback,
} from "./toolResultAccessFeedback.js?v=1.0.0";
import { getReviewFollowupPresentation } from "./reviewFollowup.js?v=1.0.1";
import { userFacingToolReceipt } from "./toolReceiptVisibility.js?v=1.0.4";
import { buildDocumentVersionDiffAsync } from './documentVersionDiff.js';

const TOOL_LABELS = Object.freeze({
  solicitar_contexto: 'Documentos necesarios',
  leer_fuente: 'Pasajes verificados',
  crear_editar_archivo: 'Documento guardado',
  revisar_documento: 'Revisión del documento',
});

const TOOL_EYEBROWS = Object.freeze({
  solicitar_contexto: 'Documentos para continuar',
  leer_fuente: 'Fuente del documento',
  crear_editar_archivo: 'Documento generado',
  revisar_documento: 'Revisión del documento',
});

const STATUS_LABELS = Object.freeze({
  running: 'En curso',
  waiting_user: 'Esperando tu selección',
  completed: 'Completado',
  partial: 'Completado con advertencias',
  failed: 'No se pudo completar',
  cancelled: 'Detenido',
  expired: 'Vencido',
  superseded: 'Reemplazado',
});

const ACTION_LABELS = Object.freeze({
  'abrir pasajes': 'Revisar pasajes',
  'seleccionar documentos': 'Elegir archivos',
  'seleccionar otros documentos': 'Elegir otros archivos',
  'abrir revision': 'Ver revisión',
  'ver revision': 'Ver revisión',
  'confirmar reemplazo': 'Confirmar reemplazo',
  'cancelar reemplazo': 'Cancelar',
  'omitir': 'Continuar sin documentos',
  'cancelar': 'Cancelar solicitud',
});

const ARTIFACT_DIFFERENCE_CACHE_LIMIT = 6;
const ARTIFACT_DIFFERENCE_START_REVEAL_PX = 44;

function normalizeActionLabel(value) {
  return String(value || '')
    .trim()
    .toLocaleLowerCase('es-ES')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export default {
  inject: {
    setProgrammaticChatScrollTop: { default: null },
  },
  props: {
    receipt: { type: Object, required: true },
    assistantReply: { type: Boolean, default: false },
    chatUuid: { type: String, default: '' },
    inlinePassages: { type: Array, default: null },
    activeReview: { type: Object, default: null },
  },
  data() {
    return {
      artifactReconciliationIdentity: '',
      artifactProjection: null,
      artifactVersions: [],
      artifactRequestKey: '',
      artifactRequestGeneration: 0,
      artifactRetryVersion: null,
      artifactLoadError: false,
      artifactLoadFailure: null,
      artifactLoadRetrying: false,
      artifactDifferenceOpen: false,
      artifactDifferenceLoading: false,
      artifactDifferenceError: '',
      artifactDifferenceModel: null,
      artifactDifferenceDocumentHtml: '',
      artifactDifferenceActiveIndex: 0,
      artifactDifferenceRequestKey: '',
      artifactDifferenceRequestSequence: 0,
      artifactDifferenceController: null,
      artifactDifferenceWorker: null,
      artifactDifferenceWorkerCancel: null,
      artifactDifferenceReturnFocus: null,
      artifactDifferenceScrollFrame: null,
      artifactDifferenceScrollReleaseTimer: null,
      artifactDifferenceProgrammaticScroll: false,
      artifactDifferenceCache: {},
      artifactActionFeedback: { key: '', phase: 'idle' },
      artifactActionFeedbackTimer: null,
    };
  },
  computed: {
    payload() {
      const displayReceipt = userFacingToolReceipt(this.receipt);
      return displayReceipt
        && displayReceipt.ui_payload
        && typeof displayReceipt.ui_payload === 'object'
        ? displayReceipt.ui_payload
        : {};
    },
    status() {
      const value = String(this.receipt.status || 'running');
      return Object.prototype.hasOwnProperty.call(STATUS_LABELS, value) ? value : 'running';
    },
    versionRetained() {
      return ((['completed', 'cancelled'].includes(this.status) && this.payload.counts?.no_changes_needed === 1)
        || (['partial', 'cancelled'].includes(this.status) && this.payload.counts?.existing_version_retained === 1))
        && Number(this.selectedArtifactVersion) === Number(this.receiptArtifactVersion);
    },
    title() {
      return String(this.payload.title || TOOL_LABELS[this.receipt.tool_name] || 'Resultado del documento').slice(0, 300);
    },
    summary() {
      const value = String(this.payload.summary || '').slice(0, 1000);
      return this.usesReviewCopy ? humanizeReviewSummary(value) : value;
    },
    warnings() {
      const values = Array.isArray(this.payload.warnings)
        ? this.payload.warnings.filter(item => typeof item === 'string').slice(0, 20)
        : [];
      if (this.artifactProjection && Array.isArray(this.artifactProjection.warnings)) {
        values.push(...this.artifactProjection.warnings.filter(item => typeof item === 'string').slice(0, 5));
      }
      return this.usesReviewCopy ? humanizeReviewWarnings(values) : values;
    },
    actions() {
      if (this.isActiveReview) return [];
      if (!Array.isArray(this.payload.actions)) return [];
      const canOpenPassages = Boolean(
        (Array.isArray(this.inlinePassages) && this.inlinePassages.length > 0)
        || (
          this.receipt
          && this.receipt.result_ref
          && this.receipt.result_ref.type === 'anchor_set'
          && this.receipt.result_ref.id
        )
      );
      return this.payload.actions
        .filter(item => typeof item === 'string')
        .filter(item => (
          normalizeActionLabel(item) !== 'abrir pasajes' || canOpenPassages
        ))
        .slice(0, 10);
    },
    statusLabel() {
      if (this.isActiveReview && this.reviewCardState) {
        return this.reviewCardState.status;
      }
      if (
        this.isReview
        && ['completed', 'partial', 'superseded'].includes(this.status)
      ) return 'Revisión disponible';
      if (
        this.receipt.tool_name === 'leer_fuente'
        && this.status === 'partial'
        && Number(this.counts.passages) === 0
      ) return 'No se encontraron pasajes';
      return STATUS_LABELS[this.status];
    },
    isBusy() {
      return this.status === 'running';
    },
    isArtifactReply() {
      return this.assistantReply
        && this.receipt.tool_name === 'crear_editar_archivo'
        && this.receipt.result_ref
        && this.receipt.result_ref.type === 'document';
    },
    isOrphanedArtifact() {
      return !this.assistantReply
        && this.receipt.tool_name === 'crear_editar_archivo'
        && this.receipt.result_ref
        && this.receipt.result_ref.type === 'document'
        && this.receipt.response_message_exists === false;
    },
    isArtifactCard() {
      return this.isArtifactReply || this.isOrphanedArtifact;
    },
    artifactId() {
      return this.receipt && this.receipt.result_ref
        ? String(this.receipt.result_ref.id || '')
        : '';
    },
    receiptArtifactVersion() {
      const version = Number(this.payload.counts && this.payload.counts.version);
      return Number.isInteger(version) && version > 0 ? version : null;
    },
    visibleArtifactWordCount() {
      const counts = this.payload.counts;
      if (!counts || typeof counts !== 'object'
        || !Object.prototype.hasOwnProperty.call(counts, 'words')) return null;
      const raw = counts.words;
      if (raw === null || typeof raw === 'boolean') return null;
      const count = Number(raw);
      if (!Number.isInteger(count) || count < 0) return null;
      return Number(this.selectedArtifactVersion) === Number(this.receiptArtifactVersion)
        ? count
        : null;
    },
    selectedArtifactVersion() {
      const version = Number(this.artifactProjection && this.artifactProjection.selected_version);
      return Number.isInteger(version) && version > 0 ? version : this.receiptArtifactVersion;
    },
    currentArtifactVersion() {
      const version = Number(this.artifactProjection && this.artifactProjection.current_version);
      return Number.isInteger(version) && version > 0 ? version : this.selectedArtifactVersion;
    },
    artifactParentVersion() {
      const projectedParent = Number(this.artifactProjection && this.artifactProjection.parent_version);
      if (Number.isInteger(projectedParent) && projectedParent > 0) return projectedParent;
      const selected = Number(this.selectedArtifactVersion);
      const selectedSlot = this.artifactVersions.find(item => (
        Number(item && item.version_number) === selected
      ));
      const slottedParent = Number(selectedSlot && selectedSlot.parent_version_number);
      return Number.isInteger(slottedParent) && slottedParent > 0 ? slottedParent : null;
    },
    artifactParentSlot() {
      const parent = Number(this.artifactParentVersion);
      if (!Number.isInteger(parent) || parent < 1) return null;
      return this.artifactVersions.find(item => (
        Number(item && item.version_number) === parent
      )) || null;
    },
    hasArtifactDifferenceComparison() {
      return !this.versionRetained && Number(this.selectedArtifactVersion) >= 2
        && Number(this.artifactParentVersion) >= 1
        && this.artifactProjection
        && this.artifactProjection.available !== false;
    },
    artifactParentUnavailable() {
      return Boolean(this.artifactParentSlot && this.artifactParentSlot.available === false);
    },
    artifactDifferenceTourVersion() {
      let candidate = null;
      let selectedCandidate = null;
      this.artifactVersions.forEach((version) => {
        const versionNumber = Number(version && version.version_number);
        const parentNumber = Number(version && version.parent_version_number);
        const parent = this.artifactVersions.find(item => (
          Number(item && item.version_number) === parentNumber
        ));
        if (
          Number.isInteger(versionNumber)
          && versionNumber >= 2
          && version.available !== false
          && parent
          && parent.available !== false
        ) {
          if (versionNumber === Number(this.selectedArtifactVersion)) {
            selectedCandidate = versionNumber;
          }
          if (candidate === null || versionNumber > candidate) candidate = versionNumber;
        }
      });
      return selectedCandidate || candidate;
    },
    hasArtifactDifferenceTour() {
      return Number(this.artifactDifferenceTourVersion) >= 2
        && this.artifactProjection
        && this.artifactProjection.available !== false;
    },
    artifactDifferenceTourState() {
      if (this.artifactDifferenceLoading) return 'loading';
      if (this.artifactDifferenceError) return 'error';
      if (this.artifactDifferenceModel) {
        return this.artifactDifferenceHunks.length ? 'changes' : 'unchanged';
      }
      return 'idle';
    },
    artifactDifferenceTourHasContext() {
      return String(this.artifactDifferenceDocumentHtml || '')
        .includes('data-tour="document-edition-context"');
    },
    artifactDifferencePanelId() {
      return `${this.artifactCardId}-differences`;
    },
    artifactDifferenceTitleId() {
      return `${this.artifactDifferencePanelId}-title`;
    },
    artifactDifferenceHunks() {
      return this.artifactDifferenceModel && Array.isArray(this.artifactDifferenceModel.hunks)
        ? this.artifactDifferenceModel.hunks
        : [];
    },
    artifactDifferenceCounts() {
      const counts = this.artifactDifferenceModel && this.artifactDifferenceModel.counts;
      return counts && typeof counts === 'object'
        ? counts
        : { additions: 0, removals: 0 };
    },
    artifactDifferenceHasWarning() {
      return Boolean(
        this.artifactDifferenceModel
        && (this.artifactDifferenceModel.truncated || this.artifactDifferenceModel.fallback)
      );
    },
    artifactTitle() {
      return String(
        (this.artifactProjection && (this.artifactProjection.title || this.artifactProjection.filename))
        || this.title
      ).slice(0, 300);
    },
    artifactCardId() {
      const suffix = String(this.receipt.tool_run_id || this.artifactId || 'document')
        .replace(/[^a-zA-Z0-9_-]/g, '-');
      return `artifact-inline-${suffix}`;
    },
    renderedArtifact() {
      if (!this.artifactProjection) return '';
      const source = String(
        this.artifactProjection.content_rendered_markdown
        || this.artifactProjection.content_markdown
        || ''
      );
      if (!source) return '';
      return this.renderArtifactMarkdown(source);
    },
    artifactProvenanceUnavailable() {
      return Boolean(
        this.artifactProjection
        && this.artifactProjection.available === false
      );
    },
    isContextRequest() {
      return this.receipt
        && this.receipt.result_ref
        && this.receipt.result_ref.type === 'context_request';
    },
    isReview() {
      return this.receipt
        && this.receipt.result_ref
        && this.receipt.result_ref.type === 'review';
    },
    activeReviewPresentation() {
      return getReviewFollowupPresentation(this.activeReview);
    },
    receiptReviewId() {
      if (!this.isReview || !this.receipt.result_ref) return '';
      return String(this.receipt.result_ref.id || '').trim();
    },
    isActiveReview() {
      const active = this.activeReviewPresentation && this.activeReviewPresentation.review;
      return Boolean(
        this.isReview
        && active
        && active.active
        && this.receiptReviewId
        && this.receiptReviewId === active.reviewId
      );
    },
    reviewCardState() {
      return this.isActiveReview && this.activeReviewPresentation
        ? this.activeReviewPresentation.card
        : null;
    },
    reviewCardExplanation() {
      return this.reviewCardState ? this.reviewCardState.explanation : '';
    },
    reviewCardActions() {
      return this.reviewCardState && Array.isArray(this.reviewCardState.actions)
        ? this.reviewCardState.actions
        : [];
    },
    usesReviewCopy() {
      return this.isReview || !!(this.receipt && this.receipt.tool_name === 'revisar_documento');
    },
    isSourceRead() {
      return this.receipt && this.receipt.tool_name === 'leer_fuente';
    },
    toolKind() {
      if (this.isContextRequest) return 'context';
      if (this.isReview) return 'review';
      if (this.receipt.tool_name === 'leer_fuente') return 'source';
      return 'result';
    },
    toolIconClass() {
      return {
        context: 'anticon anticon-file-search',
        review: 'anticon anticon-audit',
        source: 'anticon anticon-book',
        result: 'anticon anticon-check-circle',
      }[this.toolKind];
    },
    receiptTitleId() {
      const suffix = String(this.receipt.tool_run_id || this.receipt.result_ref && this.receipt.result_ref.id || 'result')
        .replace(/[^a-zA-Z0-9_-]/g, '-');
      return `tool-receipt-title-${suffix}`;
    },
    counts() {
      return this.payload.counts && typeof this.payload.counts === 'object'
        ? this.payload.counts
        : {};
    },
    reviewStats() {
      if (!this.isReview || this.isActiveReview) return [];
      const confirmed = Math.max(0, Number(this.counts.confirmed) || 0);
      const potential = Math.max(0, Number(this.counts.potential) || 0);
      const reviewedAspects = Math.max(0, Number(this.counts.reviewed_aspects) || 0);
      const legalSources = Math.max(0, Number(this.counts.legal_sources) || 0);
      return [
        { label: 'Hallazgos', value: confirmed + potential },
        { label: 'Aspectos', value: reviewedAspects },
        { label: 'Fuentes', value: legalSources },
      ];
    },
    sourceStats() {
      if (!this.isSourceRead) return [];
      const hasCount = key => Object.prototype.hasOwnProperty.call(this.counts, key);
      const stat = (label, key, tone = '') => {
        if (!hasCount(key)) return null;
        return {
          label,
          value: Math.max(0, Number(this.counts[key]) || 0),
          tone,
        };
      };
      return [
        stat('Pasajes', 'passages', 'verified'),
        stat('Fuentes', 'sources_matched', 'verified'),
      ].filter(Boolean);
    },
    requestedDocuments() {
      const values = this.payload.requested_documents || this.payload.requests;
      if (!Array.isArray(values)) return [];
      return values.map((item) => {
        if (typeof item === 'string') {
          return {
            requestItemId: '',
            label: item.slice(0, 300),
            detail: '',
            priority: 'recomendado',
            acceptedTypes: ['documento'],
          };
        }
        if (!item || typeof item !== 'object') return null;
        const label = String(item.label || item.title || item.documento || item.name || item.tipo || '').slice(0, 300);
        const rawDetail = String(item.description || item.descripcion || item.detail || '').slice(0, 500);
        const detail = rawDetail.trim().toLocaleLowerCase('es-ES') === label.trim().toLocaleLowerCase('es-ES')
          ? ''
          : rawDetail;
        const acceptedTypes = (Array.isArray(item.accepted_types) ? item.accepted_types : ['documento'])
          .map(value => ({ document: 'documento', image: 'imagen' }[
            String(value || '').trim().toLocaleLowerCase('es-ES')
          ] || String(value || '').trim().toLocaleLowerCase('es-ES')))
          .filter(value => ['documento', 'imagen', 'audio', 'video'].includes(value));
        return label ? {
          requestItemId: String(item.request_item_id || item.id || ''),
          label,
          detail,
          priority: String(item.priority || item.prioridad || '').toLocaleLowerCase('es-ES') === 'necesario'
            ? 'necesario'
            : 'recomendado',
          acceptedTypes: acceptedTypes.length ? [...new Set(acceptedTypes)] : ['documento'],
        } : null;
      }).filter(Boolean).slice(0, 10);
    },
    requestedDocumentsHeading() {
      return this.requestedDocuments.length === 1
        ? 'Documento solicitado'
        : 'Documentos solicitados';
    },
    eyebrow() {
      if (
        this.receipt.tool_name === 'crear_editar_archivo'
        && this.receipt.status === 'failed'
      ) return 'Documento no guardado';
      return TOOL_EYEBROWS[this.receipt.tool_name] || 'Resultado de la conversación';
    },
  },
  watch: {
    receipt: {
      immediate: true,
      handler() {
        const identity = `${this.chatUuid}:${this.receipt?.tool_run_id || ''}:${this.artifactId}:${this.receiptArtifactVersion || ''}`;
        if (identity === this.artifactReconciliationIdentity) return;
        this.artifactReconciliationIdentity = identity;
        this.resetArtifactProjection();
      },
    },
    assistantReply() {
      this.resetArtifactProjection();
    },
    chatUuid() {
      this.resetArtifactProjection();
    },
  },
  beforeUnmount() {
    this.artifactRequestGeneration += 1;
    if (this.artifactActionFeedbackTimer) clearTimeout(this.artifactActionFeedbackTimer);
    this.disposeArtifactHelpPopover();
    this.cancelArtifactDifferenceScrollFrame();
    this.abortArtifactDifferenceRequest();
  },
  methods: {
    clearArtifactActionFeedbackTimer() {
      if (this.artifactActionFeedbackTimer) {
        clearTimeout(this.artifactActionFeedbackTimer);
        this.artifactActionFeedbackTimer = null;
      }
    },
    artifactActionCopy(key, phase = this.artifactActionFeedback.phase) {
      const messages = {
        copy: {
          pending: 'Copiando…',
          success: 'Copiado',
          error: 'No se pudo copiar',
        },
        pdf: {
          pending: 'Preparando PDF…',
          success: 'PDF descargado',
          error: 'No se pudo descargar el PDF',
        },
        docx: {
          pending: 'Preparando Word…',
          success: 'Word descargado',
          error: 'No se pudo descargar el Word',
        },
      };
      return messages[key] && messages[key][phase] ? messages[key][phase] : '';
    },
    artifactActionButtonClass(key) {
      if (this.artifactActionFeedback.key !== key) return {};
      return {
        'is-action-pending': this.artifactActionFeedback.phase === 'pending',
        'is-action-success': this.artifactActionFeedback.phase === 'success',
        'is-action-error': this.artifactActionFeedback.phase === 'error',
      };
    },
    artifactActionIcon(key, defaultIcon) {
      if (this.artifactActionFeedback.key !== key) return defaultIcon;
      if (this.artifactActionFeedback.phase === 'pending') return 'anticon anticon-loading';
      if (this.artifactActionFeedback.phase === 'success') return 'anticon anticon-check';
      if (this.artifactActionFeedback.phase === 'error') return 'anticon anticon-warning';
      return defaultIcon;
    },
    artifactActionLabel(key, defaultLabel) {
      if (this.artifactActionFeedback.key !== key) return defaultLabel;
      return this.artifactActionCopy(key) || defaultLabel;
    },
    emitArtifactAction(action, key) {
      if (this.artifactActionFeedback.phase === 'pending') return;
      this.clearArtifactActionFeedbackTimer();
      this.artifactActionFeedback = { key, phase: 'pending' };
      let settled = false;
      const complete = (successful = false) => {
        if (settled) return;
        settled = true;
        if (this.artifactActionFeedback.key !== key) return;
        this.artifactActionFeedback = {
          key,
          phase: successful ? 'success' : 'error',
        };
        this.artifactActionFeedbackTimer = setTimeout(() => {
          if (this.artifactActionFeedback.key === key) {
            this.artifactActionFeedback = { key: '', phase: 'idle' };
          }
          this.artifactActionFeedbackTimer = null;
        }, 2200);
      };
      const artifactClipboardContent = key === 'copy' && this.artifactProjection
        ? String(
          this.artifactProjection.content_export_markdown
          || this.artifactProjection.content_rendered_markdown
          || this.artifactProjection.content_markdown
          || ''
        )
        : '';
      this.$emit('action', {
        action: String(action || ''),
        receipt: this.receipt,
        artifactVersion: this.selectedArtifactVersion || null,
        artifactClipboardContent,
        complete,
      });
    },
    initializeArtifactHelpPopover() {
      const trigger = this.$refs.artifactHelpButton;
      if (!trigger || !this.hasArtifactDifferenceComparison) return;
      const content = String(trigger.getAttribute('data-content') || '').trim();
      const container = trigger.parentElement || 'body';
      try {
        if (window.jQuery && typeof window.jQuery(trigger).popover === 'function') {
          const popoverTrigger = window.jQuery(trigger);
          if (!popoverTrigger.data('bs.popover')) {
            popoverTrigger.popover({
              trigger: 'click',
              html: true,
              placement: 'bottom',
              container,
              boundary: 'viewport',
              customClass: 'lexius-help-popover--document-edition',
              content,
            });
          }
          return;
        }
        const Popover = window.bootstrap && window.bootstrap.Popover;
        if (!Popover) return;
        if (typeof Popover.getOrCreateInstance === 'function') {
          Popover.getOrCreateInstance(trigger, {
            trigger: 'click',
            html: true,
            placement: 'bottom',
            container,
            boundary: 'viewport',
            customClass: 'lexius-help-popover--document-edition',
            content,
          });
        } else if (!Popover.getInstance || !Popover.getInstance(trigger)) {
          new Popover(trigger, {
            trigger: 'click',
            html: true,
            placement: 'bottom',
            container,
            boundary: 'viewport',
            customClass: 'lexius-help-popover--document-edition',
            content,
          });
        }
      } catch (error) { }
    },
    disposeArtifactHelpPopover() {
      const trigger = this.$refs.artifactHelpButton;
      if (!trigger) return;
      try {
        const instance = window.bootstrap?.Popover?.getInstance?.(trigger);
        if (instance?.dispose) instance.dispose();
      } catch (error) { }
      try {
        if (window.jQuery && typeof window.jQuery(trigger).popover === 'function') {
          const popoverTrigger = window.jQuery(trigger);
          if (popoverTrigger.data('bs.popover')) popoverTrigger.popover('dispose');
        }
      } catch (error) { }
    },
    abortArtifactDifferenceRequest() {
      if (this.artifactDifferenceController) {
        try {
          this.artifactDifferenceController.abort();
        } catch (error) { }
      }
      if (this.artifactDifferenceWorkerCancel) {
        this.artifactDifferenceWorkerCancel();
      } else if (this.artifactDifferenceWorker) {
        this.artifactDifferenceWorker.terminate();
      }
      this.artifactDifferenceController = null;
      this.artifactDifferenceWorker = null;
      this.artifactDifferenceWorkerCancel = null;
      this.artifactDifferenceRequestKey = '';
    },
    async calculateArtifactDifference(previousMarkdown, nextMarkdown, requestKey, options = {}) {
      const diffOptions = options && typeof options === 'object' ? options : {};
      if (typeof Worker !== 'function') {
        return buildDocumentVersionDiffAsync(previousMarkdown, nextMarkdown, diffOptions);
      }
      return new Promise((resolve, reject) => {
        let settled = false;
        let worker;
        const finish = () => {
          if (settled) return false;
          settled = true;
          if (worker) worker.terminate();
          if (this.artifactDifferenceWorker === worker) {
            this.artifactDifferenceWorker = null;
            this.artifactDifferenceWorkerCancel = null;
          }
          return true;
        };
        const cancel = () => {
          if (!finish()) return;
          const error = new Error('difference_cancelled');
          error.name = 'AbortError';
          reject(error);
        };
        try {
          worker = new Worker(
            new URL('./documentVersionDiff.worker.js', import.meta.url),
            { type: 'module' },
          );
          this.artifactDifferenceWorker = worker;
          this.artifactDifferenceWorkerCancel = cancel;
          worker.addEventListener('message', (event) => {
            const payload = event && event.data && typeof event.data === 'object'
              ? event.data
              : {};
            if (String(payload.requestKey || '') !== requestKey || !finish()) return;
            if (payload.error || !payload.model) {
              reject(new Error('difference_unavailable'));
              return;
            }
            resolve(payload.model);
          });
          worker.addEventListener('error', (event) => {
            if (!finish()) return;
            if (event && typeof event.preventDefault === 'function') event.preventDefault();
            buildDocumentVersionDiffAsync(previousMarkdown, nextMarkdown, diffOptions)
              .then(resolve, reject);
          });
          worker.postMessage({
            requestKey,
            previousMarkdown,
            nextMarkdown,
            previousBlocks: diffOptions.previousBlocks,
            nextBlocks: diffOptions.nextBlocks,
          });
        } catch (error) {
          if (worker) worker.terminate();
          this.artifactDifferenceWorker = null;
          this.artifactDifferenceWorkerCancel = null;
          buildDocumentVersionDiffAsync(previousMarkdown, nextMarkdown, diffOptions)
            .then(resolve, reject);
        }
      });
    },
    resetArtifactDifferenceState({ clearCache = false } = {}) {
      this.cancelArtifactDifferenceScrollFrame();
      this.abortArtifactDifferenceRequest();
      this.artifactDifferenceOpen = false;
      this.artifactDifferenceLoading = false;
      this.artifactDifferenceError = '';
      this.artifactDifferenceModel = null;
      this.artifactDifferenceDocumentHtml = '';
      this.artifactDifferenceActiveIndex = 0;
      this.artifactDifferenceReturnFocus = null;
      if (clearCache) this.artifactDifferenceCache = {};
    },
    artifactDifferenceCacheKey() {
      const parentHash = String(this.artifactParentSlot && this.artifactParentSlot.content_hash || '');
      const selectedHash = String(this.artifactProjection && this.artifactProjection.content_hash || '');
      return [
        this.chatUuid,
        this.artifactId,
        this.artifactParentVersion,
        this.selectedArtifactVersion,
        parentHash,
        selectedHash,
      ].join(':');
    },
    focusArtifactDifferencePanel() {
      this.$nextTick(() => {
        const panel = this.$el && this.$el.querySelector
          ? this.$el.querySelector(`#${this.artifactDifferencePanelId}`)
          : null;
        if (panel && typeof panel.focus === 'function') panel.focus({ preventScroll: true });
      });
    },
    closeArtifactDifferences({ restoreFocus = true, clearResult = false } = {}) {
      const returnFocus = this.artifactDifferenceReturnFocus;
      this.cancelArtifactDifferenceScrollFrame();
      this.abortArtifactDifferenceRequest();
      this.artifactDifferenceOpen = false;
      this.artifactDifferenceLoading = false;
      this.artifactDifferenceError = '';
      this.artifactDifferenceActiveIndex = 0;
      if (clearResult) {
        this.artifactDifferenceModel = null;
        this.artifactDifferenceDocumentHtml = '';
      }
      this.artifactDifferenceReturnFocus = null;
      if (restoreFocus && returnFocus && returnFocus.isConnected) {
        this.$nextTick(() => returnFocus.focus({ preventScroll: true }));
      }
    },
    setArtifactDifferenceTourState(event) {
      const shouldOpen = Boolean(event && event.detail && event.detail.open === true);
      if (shouldOpen) {
        if (!this.artifactDifferenceOpen) void this.openArtifactDifferences(null);
        return;
      }
      if (this.artifactDifferenceOpen) {
        this.closeArtifactDifferences({ restoreFocus: false });
      }
    },
    async toggleArtifactDifferences(event) {
      if (this.artifactDifferenceOpen) {
        this.closeArtifactDifferences();
        return;
      }
      await this.openArtifactDifferences(event && event.currentTarget ? event.currentTarget : null);
    },
    async openArtifactDifferences(returnFocus = null, { force = false } = {}) {
      if (!this.hasArtifactDifferenceComparison || this.artifactParentUnavailable) return;
      const selectedVersion = Number(this.selectedArtifactVersion);
      const parentVersion = Number(this.artifactParentVersion);
      const selectedContent = this.artifactProjection
        && this.artifactProjection.content_markdown;
      if (typeof selectedContent !== 'string' || selectedVersion < 2 || parentVersion < 1) return;
      const selectedMarkdown = selectedContent;
      const selectedDisplayMarkdown = String(
        this.artifactProjection.content_rendered_markdown || selectedMarkdown,
      );

      this.cancelArtifactDifferenceScrollFrame();
      this.abortArtifactDifferenceRequest();
      this.artifactDifferenceOpen = true;
      this.artifactDifferenceLoading = true;
      this.artifactDifferenceError = '';
      this.artifactDifferenceDocumentHtml = '';
      this.artifactDifferenceActiveIndex = 0;
      if (returnFocus) this.artifactDifferenceReturnFocus = returnFocus;
      const cacheKey = this.artifactDifferenceCacheKey();
      if (!force && Object.prototype.hasOwnProperty.call(this.artifactDifferenceCache, cacheKey)) {
        const cached = this.artifactDifferenceCache[cacheKey];
        this.artifactDifferenceModel = cached && cached.model;
        this.artifactDifferenceDocumentHtml = String(cached && cached.documentHtml || '');
        this.artifactDifferenceLoading = false;
        this.focusArtifactDifferencePanel();
        this.$nextTick(() => {
          this.goToArtifactDifference(0, {
            behavior: 'auto',
            focus: false,
            initialOpen: true,
          });
          this.attachArtifactLinks();
        });
        return;
      }

      this.artifactDifferenceRequestSequence += 1;
      const requestKey = `${cacheKey}:${this.artifactDifferenceRequestSequence}`;
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      this.artifactDifferenceRequestKey = requestKey;
      this.artifactDifferenceController = controller;
      this.focusArtifactDifferencePanel();
      const query = new URLSearchParams({
        chat_uuid: this.chatUuid,
        version: String(parentVersion),
      });
      try {
        const response = await fetch(
          `/chat/document-artifacts/${encodeURIComponent(this.artifactId)}?${query.toString()}`,
          controller ? { signal: controller.signal } : {},
        );
        const previousProjection = await response.json().catch(() => ({}));
        if (this.artifactDifferenceRequestKey !== requestKey) return;
        if (!response.ok) throw new Error('previous_version_load_failed');
        if (previousProjection.available === false) throw new Error('previous_version_unavailable');
        if (Number(previousProjection.selected_version) !== parentVersion) {
          throw new Error('previous_version_mismatch');
        }
        if (typeof previousProjection.content_markdown !== 'string') {
          throw new Error('previous_version_load_failed');
        }
        const previousMarkdown = String(previousProjection.content_markdown);
        const previousStream = this.tokenizeArtifactDifferenceMarkdown(previousMarkdown);
        const selectedStream = this.tokenizeArtifactDifferenceMarkdown(selectedMarkdown);
        const displayStream = this.tokenizeArtifactDifferenceMarkdown(
          selectedDisplayMarkdown,
          { prepareForDisplay: true },
        );

        await new Promise(resolve => {
          if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(() => resolve());
          } else {
            setTimeout(resolve, 0);
          }
        });
        if (this.artifactDifferenceRequestKey !== requestKey) return;
        const model = await this.calculateArtifactDifference(
          previousMarkdown,
          selectedMarkdown,
          requestKey,
          previousStream && selectedStream ? {
            previousBlocks: previousStream.blocks,
            nextBlocks: selectedStream.blocks,
          } : {},
        );
        if (this.artifactDifferenceRequestKey !== requestKey) return;
        if (
          !model
          || !Array.isArray(model.hunks)
          || !Array.isArray(model.sections)
        ) throw new Error('difference_unavailable');
        const documentHtml = this.buildArtifactDifferenceDocumentHtml(
          model,
          selectedStream,
          displayStream || selectedStream,
        );
        if (model.sections.length && !documentHtml) throw new Error('difference_unavailable');
        this.artifactDifferenceModel = model;
        this.artifactDifferenceDocumentHtml = documentHtml;
        const nextCache = {
          ...this.artifactDifferenceCache,
          [cacheKey]: { model, documentHtml },
        };
        const cacheKeys = Object.keys(nextCache);
        while (cacheKeys.length > ARTIFACT_DIFFERENCE_CACHE_LIMIT) {
          delete nextCache[cacheKeys.shift()];
        }
        this.artifactDifferenceCache = nextCache;
        this.$nextTick(() => {
          this.goToArtifactDifference(0, {
            behavior: 'auto',
            focus: false,
            initialOpen: true,
          });
          this.attachArtifactLinks();
        });
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        if (this.artifactDifferenceRequestKey !== requestKey) return;
        this.artifactDifferenceModel = null;
        this.artifactDifferenceDocumentHtml = '';
        this.artifactDifferenceError = error && error.message === 'previous_version_unavailable'
          ? 'La versión anterior no está disponible para comparar.'
          : 'No se pudieron calcular las diferencias. Intenta nuevamente.';
      } finally {
        if (this.artifactDifferenceRequestKey === requestKey) {
          this.artifactDifferenceLoading = false;
          this.artifactDifferenceController = null;
        }
      }
    },
    retryArtifactDifferences() {
      return this.openArtifactDifferences(this.artifactDifferenceReturnFocus, { force: true });
    },
    artifactDifferenceHunkId(index) {
      return `${this.artifactDifferencePanelId}-change-${Number(index) + 1}`;
    },
    artifactDifferenceKind(hunk) {
      if (hunk && hunk.hasAddition && hunk.hasRemoval) return 'Texto modificado';
      if (hunk && hunk.hasAddition) return 'Texto añadido';
      return 'Texto eliminado';
    },
    tokenizeArtifactDifferenceMarkdown(value, { prepareForDisplay = false } = {}) {
      const browserWindow = typeof window !== 'undefined' ? window : null;
      const renderer = browserWindow && browserWindow.marked;
      if (!renderer || typeof renderer.lexer !== 'function') return null;
      try {
        const rawSource = String(value || '').replace(/\r\n/g, '\n').normalize('NFC');
        const preparedSource = prepareForDisplay
          && browserWindow
          && typeof browserWindow.processMarkdownContent === 'function'
          ? browserWindow.processMarkdownContent(rawSource)
          : rawSource;
        const source = String(preparedSource || '').replace(/\r\n/g, '\n').normalize('NFC');
        const lexedTokens = renderer.lexer(source);
        if (!Array.isArray(lexedTokens)) return null;
        const tokens = [];
        let sourceOffset = 0;
        // Marked keeps reference-link definitions in `tokens.links` but omits
        // them from the visible top-level token array. Retain every omitted
        // source gap as a non-rendering space token so block offsets can still
        // reconstruct and compare the canonical Markdown exactly.
        lexedTokens.forEach((token) => {
          const raw = String(token && token.raw || '');
          if (!raw) return;
          const tokenOffset = source.startsWith(raw, sourceOffset)
            ? sourceOffset
            : source.indexOf(raw, sourceOffset);
          if (tokenOffset < sourceOffset) throw new Error('markdown_token_alignment_failed');
          if (tokenOffset > sourceOffset) {
            const gap = source.slice(sourceOffset, tokenOffset);
            tokens.push({
              type: 'space',
              raw: gap,
              text: '',
              artifactSourceGap: true,
            });
          }
          tokens.push(token);
          sourceOffset = tokenOffset + raw.length;
        });
        if (sourceOffset < source.length) {
          tokens.push({
            type: 'space',
            raw: source.slice(sourceOffset),
            text: '',
            artifactSourceGap: true,
          });
        }
        tokens.links = lexedTokens.links || {};
        const blocks = tokens.map(token => String(token && token.raw || ''));
        if (blocks.join('') !== source) return null;
        return { source, tokens, blocks };
      } catch (error) {
        return null;
      }
    },
    alignArtifactDifferenceDisplayTokens(canonicalStream, displayStream) {
      const canonicalTokens = canonicalStream && Array.isArray(canonicalStream.tokens)
        ? canonicalStream.tokens
        : [];
      const displayTokens = displayStream && Array.isArray(displayStream.tokens)
        ? displayStream.tokens
        : [];
      const aligned = canonicalTokens.slice();
      if (!canonicalTokens.length || !displayTokens.length) return aligned;

      const sameTokenType = (left, right) => (
        String(left && left.type || '') === String(right && right.type || '')
      );
      if (
        canonicalTokens.length === displayTokens.length
        && canonicalTokens.every((token, index) => sameTokenType(token, displayTokens[index]))
      ) return displayTokens.slice();

      const tokenKey = token => `${String(token && token.type || '')}\u0000${String(token && token.raw || '')}`;
      const uniquePositions = (tokens) => {
        const positions = new Map();
        tokens.forEach((token, index) => {
          const key = tokenKey(token);
          positions.set(key, positions.has(key) ? null : index);
        });
        return positions;
      };
      const canonicalPositions = uniquePositions(canonicalTokens);
      const displayPositions = uniquePositions(displayTokens);
      const anchors = [];
      let lastDisplayIndex = -1;
      canonicalTokens.forEach((token, canonicalIndex) => {
        const key = tokenKey(token);
        if (canonicalPositions.get(key) !== canonicalIndex) return;
        const displayIndex = displayPositions.get(key);
        if (!Number.isInteger(displayIndex) || displayIndex <= lastDisplayIndex) return;
        anchors.push({ canonicalIndex, displayIndex });
        lastDisplayIndex = displayIndex;
      });

      const boundaries = [
        { canonicalIndex: -1, displayIndex: -1 },
        ...anchors,
        { canonicalIndex: canonicalTokens.length, displayIndex: displayTokens.length },
      ];
      for (let boundaryIndex = 0; boundaryIndex < boundaries.length - 1; boundaryIndex += 1) {
        const left = boundaries[boundaryIndex];
        const right = boundaries[boundaryIndex + 1];
        if (right.canonicalIndex < canonicalTokens.length) {
          aligned[right.canonicalIndex] = displayTokens[right.displayIndex];
        }
        const canonicalStart = left.canonicalIndex + 1;
        const displayStart = left.displayIndex + 1;
        const canonicalCount = right.canonicalIndex - canonicalStart;
        const displayCount = right.displayIndex - displayStart;
        if (canonicalCount !== displayCount) continue;
        for (let offset = 0; offset < canonicalCount; offset += 1) {
          const canonicalToken = canonicalTokens[canonicalStart + offset];
          const displayToken = displayTokens[displayStart + offset];
          if (!sameTokenType(canonicalToken, displayToken)) break;
          aligned[canonicalStart + offset] = displayToken;
        }
      }
      return aligned;
    },
    artifactDifferenceHtmlToken(html) {
      const value = String(html || '');
      return {
        type: 'html',
        raw: value,
        text: value,
        block: true,
        pre: false,
      };
    },
    artifactDifferencePassageHtml(segments, changedType) {
      const values = Array.isArray(segments) ? segments : [];
      if (!values.length) {
        const message = changedType === 'added'
          ? 'Este pasaje no existía.'
          : 'Este pasaje fue eliminado.';
        return `<p class="artifact-difference__passage artifact-difference__passage--empty">${message}</p>`;
      }
      const content = values.map((segment) => {
        const type = String(segment && segment.type || 'equal');
        const value = this.displayArtifactDifferenceSegment(segment && segment.value, type);
        const escaped = escapePlaintextHtml(value);
        if (type === changedType) {
          const tag = changedType === 'added' ? 'ins' : 'del';
          return `<${tag} data-diff-segment="${changedType}">${escaped}</${tag}>`;
        }
        return `<span>${escaped}</span>`;
      }).join('');
      return `<p class="artifact-difference__passage">${content}</p>`;
    },
    artifactDifferenceChangeHtml(hunk, hunkIndex, hunkCount = 0) {
      const safeIndex = Number.isInteger(hunkIndex) && hunkIndex >= 0 ? hunkIndex : 0;
      const safeCount = Number.isInteger(hunkCount) && hunkCount > 0 ? hunkCount : safeIndex + 1;
      const kind = this.artifactDifferenceKind(hunk);
      const activeClass = safeIndex === 0 ? ' is-active' : '';
      const activeAria = safeIndex === 0 ? ' aria-current="step"' : '';
      return [
        `<section id="${escapePlaintextHtml(this.artifactDifferenceHunkId(safeIndex))}"`,
        ` class="artifact-difference__change${activeClass}"${activeAria}`,
        ` aria-label="${escapePlaintextHtml(`Cambio ${safeIndex + 1} de ${safeCount}: ${kind}`)}"`,
        ' data-testid="artifact-diff-change"',
        ' data-tour="document-edition-change"',
        ` data-diff-index="${safeIndex}" tabindex="-1" role="group">`,
        `<div class="artifact-difference__rail" aria-hidden="true"><span>${safeIndex + 1}</span></div>`,
        '<div class="artifact-difference__change-body">',
        `<span class="artifact-difference__kind">${escapePlaintextHtml(`Cambio ${safeIndex + 1} · ${kind}`)}</span>`,
        '<div class="artifact-difference__pair">',
        '<section class="artifact-difference__version artifact-difference__version--before">',
        `<header><span>Antes</span><small>v${Number(this.artifactParentVersion) || 1}</small></header>`,
        this.artifactDifferencePassageHtml(hunk && hunk.before, 'removed'),
        '</section>',
        '<section class="artifact-difference__version artifact-difference__version--after">',
        `<header><span>Ahora</span><small>v${Number(this.selectedArtifactVersion) || 1}</small></header>`,
        this.artifactDifferencePassageHtml(hunk && hunk.after, 'added'),
        '</section>',
        '</div></div></section>',
      ].join('');
    },
    buildArtifactDifferenceDocumentHtml(model, canonicalStream, displayStream) {
      const sections = model && Array.isArray(model.sections) ? model.sections : [];
      const hunks = model && Array.isArray(model.hunks) ? model.hunks : [];
      if (!sections.length) return '';
      const browserWindow = typeof window !== 'undefined' ? window : null;
      const renderer = browserWindow && browserWindow.marked;
      const parser = renderer && typeof renderer.parser === 'function'
        ? renderer.parser.bind(renderer)
        : null;
      const alignedTokens = this.alignArtifactDifferenceDisplayTokens(
        canonicalStream,
        displayStream,
      );
      const combinedTokens = [];
      const fallbackParts = [];

      sections.forEach((section) => {
        if (section && section.type === 'equal') {
          const value = String(section.value || '');
          fallbackParts.push(value);
          const start = Number(section.afterBlockStart);
          const count = Number(section.blockCount);
          const contextTokens = Number.isInteger(start) && start >= 0
            && Number.isInteger(count) && count >= 0
            ? alignedTokens.slice(start, start + count)
            : [];
          const contextMatches = contextTokens.length === count
            && canonicalStream
            && canonicalStream.tokens.slice(start, start + count)
              .map(token => String(token && token.raw || '')).join('') === value;
          combinedTokens.push(this.artifactDifferenceHtmlToken(
            '<div class="artifact-difference__context lexiusdoc-card__content" data-testid="artifact-diff-context" data-tour="document-edition-context">',
          ));
          if (parser && contextMatches) {
            combinedTokens.push(...contextTokens);
          } else {
            combinedTokens.push(this.artifactDifferenceHtmlToken(
              `<p>${escapePlaintextHtml(value)}</p>`,
            ));
          }
          combinedTokens.push(this.artifactDifferenceHtmlToken('</div>'));
          return;
        }
        const hunkIndex = Number(section && section.hunkIndex);
        const hunk = Number.isInteger(hunkIndex) && hunkIndex >= 0 ? hunks[hunkIndex] : null;
        if (!hunk) return;
        fallbackParts.push(...(Array.isArray(hunk.after)
          ? hunk.after.map(segment => String(segment && segment.value || ''))
          : []));
        combinedTokens.push(this.artifactDifferenceHtmlToken(
          this.artifactDifferenceChangeHtml(hunk, hunkIndex, hunks.length),
        ));
      });

      try {
        let html;
        if (parser) {
          combinedTokens.links = displayStream && displayStream.tokens
            ? displayStream.tokens.links || {}
            : canonicalStream && canonicalStream.tokens
              ? canonicalStream.tokens.links || {}
              : {};
          html = parser(combinedTokens);
        } else {
          html = combinedTokens.map(token => token.raw).join('');
        }
        const plaintextFallback = fallbackParts.join('');
        const transformed = browserWindow && typeof browserWindow.processConvertedHTML === 'function'
          ? browserWindow.processConvertedHTML(html, plaintextFallback)
          : html;
        return sanitizeRichHtml(transformed, plaintextFallback);
      } catch (error) {
        return sanitizeRichHtml(
          combinedTokens.map(token => token.raw).join(''),
          fallbackParts.join(''),
        );
      }
    },
    renderArtifactMarkdown(value, referenceLinks = null) {
      const source = String(value || '');
      if (!source) return '';
      try {
        const browserWindow = typeof window !== 'undefined' ? window : null;
        const processed = browserWindow && typeof browserWindow.processMarkdownContent === 'function'
          ? browserWindow.processMarkdownContent(source)
          : source;
        const renderer = browserWindow
          && browserWindow.marked
          && typeof browserWindow.marked.parse === 'function'
          ? browserWindow.marked
          : null;
        let html;
        if (renderer && referenceLinks && typeof renderer.Lexer === 'function' && typeof renderer.parser === 'function') {
          const lexer = new renderer.Lexer();
          Object.assign(lexer.tokens.links, referenceLinks);
          html = renderer.parser(lexer.lex(processed));
        } else {
          html = renderer ? renderer.parse(processed) : escapePlaintextHtml(source);
        }
        const transformed = browserWindow && typeof browserWindow.processConvertedHTML === 'function'
          ? browserWindow.processConvertedHTML(html, source)
          : html;
        return sanitizeRichHtml(transformed, source);
      } catch (error) {
        return escapePlaintextHtml(source);
      }
    },
    displayArtifactDifferenceSegment(value, type = 'equal') {
      const text = String(value || '');
      if (type === 'added' || type === 'removed') return text;
      return text
        .replace(/(^|\n)[\t ]{0,3}#{1,6}[\t ]+/g, '$1')
        .replace(/(^|\n)[\t ]*[-+*][\t ]+/g, '$1• ')
        .replace(/(^|\n)[\t ]*>[\t ]?/g, '$1')
        .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
        .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
        .replace(/\*\*|__|~~|`/g, '')
        .replace(/(^|\n)[\t ]*\|[\t ]*/g, '$1')
        .replace(/[\t ]*\|[\t ]*(?=\n|$)/g, '')
        .replace(/[\t ]+\|[\t ]+/g, ' · ');
    },
    selectArtifactDifferenceChange(event) {
      const container = event && event.currentTarget;
      const target = event && event.target && typeof event.target.closest === 'function'
        ? event.target.closest('[data-diff-index]')
        : null;
      if (!target || !container || !container.contains(target)) return;
      const index = Number(target.getAttribute('data-diff-index'));
      if (!Number.isInteger(index) || index < 0 || index >= this.artifactDifferenceHunks.length) return;
      this.artifactDifferenceActiveIndex = index;
      this.$nextTick(() => this.syncArtifactDifferenceSelection());
    },
    syncArtifactDifferenceSelection() {
      const panel = this.$el && this.$el.querySelector
        ? this.$el.querySelector(`#${this.artifactDifferencePanelId}`)
        : null;
      if (!panel || typeof panel.querySelectorAll !== 'function') return;
      panel.querySelectorAll('[data-diff-index]').forEach((element) => {
        const isActive = Number(element.getAttribute('data-diff-index'))
          === Number(this.artifactDifferenceActiveIndex);
        element.classList.toggle('is-active', isActive);
        if (isActive) element.setAttribute('aria-current', 'step');
        else element.removeAttribute('aria-current');
      });
    },
    cancelArtifactDifferenceScrollFrame() {
      if (
        this.artifactDifferenceScrollFrame !== null
        && typeof window !== 'undefined'
        && typeof window.cancelAnimationFrame === 'function'
      ) {
        window.cancelAnimationFrame(this.artifactDifferenceScrollFrame);
      }
      this.artifactDifferenceScrollFrame = null;
      if (this.artifactDifferenceScrollReleaseTimer !== null) {
        clearTimeout(this.artifactDifferenceScrollReleaseTimer);
      }
      this.artifactDifferenceScrollReleaseTimer = null;
      this.artifactDifferenceProgrammaticScroll = false;
    },
    handleArtifactDifferenceScroll(event) {
      const container = event && event.currentTarget;
      if (
        !container
        || this.artifactDifferenceProgrammaticScroll
        || this.artifactDifferenceScrollFrame !== null
      ) return;
      const browserWindow = typeof window !== 'undefined' ? window : null;
      if (!browserWindow || typeof browserWindow.requestAnimationFrame !== 'function') {
        this.syncArtifactDifferenceFromScroll(container);
        return;
      }
      this.artifactDifferenceScrollFrame = browserWindow.requestAnimationFrame(() => {
        this.artifactDifferenceScrollFrame = null;
        if (
          !this.artifactDifferenceOpen
          || this.artifactDifferenceProgrammaticScroll
          || !container.isConnected
        ) return;
        this.syncArtifactDifferenceFromScroll(container);
      });
    },
    handleArtifactDifferenceManualScrollIntent() {
      this.cancelArtifactDifferenceScrollFrame();
    },
    handleArtifactDifferenceManualScrollKey(event) {
      const key = String(event && event.key || '');
      if (!['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(key)) return;
      this.handleArtifactDifferenceManualScrollIntent();
    },
    syncArtifactDifferenceFromScroll(container) {
      if (!container || typeof container.querySelectorAll !== 'function') return;
      const viewport = container.getBoundingClientRect();
      let mostVisibleIndex = -1;
      let mostVisibleArea = 0;
      container.querySelectorAll('[data-diff-index]').forEach((element) => {
        const index = Number(element.getAttribute('data-diff-index'));
        if (!Number.isInteger(index) || index < 0 || index >= this.artifactDifferenceHunks.length) return;
        const bounds = element.getBoundingClientRect();
        const visibleHeight = Math.max(
          0,
          Math.min(bounds.bottom, viewport.bottom) - Math.max(bounds.top, viewport.top),
        );
        const visibleWidth = Math.max(
          0,
          Math.min(bounds.right, viewport.right) - Math.max(bounds.left, viewport.left),
        );
        const visibleArea = visibleHeight * visibleWidth;
        if (visibleArea > mostVisibleArea) {
          mostVisibleArea = visibleArea;
          mostVisibleIndex = index;
        }
      });
      if (mostVisibleIndex < 0 || mostVisibleIndex === this.artifactDifferenceActiveIndex) return;
      this.artifactDifferenceActiveIndex = mostVisibleIndex;
      this.syncArtifactDifferenceSelection();
    },
    goToArtifactDifference(index, options = {}) {
      const lastIndex = this.artifactDifferenceHunks.length - 1;
      if (lastIndex < 0) return null;
      const requestedIndex = Number(index);
      const safeIndex = Number.isFinite(requestedIndex) ? Math.trunc(requestedIndex) : 0;
      const nextIndex = Math.min(lastIndex, Math.max(0, safeIndex));
      const navigationOptions = options && typeof options === 'object' ? options : {};
      const shouldFocus = navigationOptions.focus !== false;
      const shouldScroll = navigationOptions.scroll !== false;
      const isInitialOpen = navigationOptions.initialOpen === true;
      const reduceMotion = typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const scrollBehavior = isInitialOpen || reduceMotion || navigationOptions.behavior === 'auto'
        ? 'auto'
        : 'smooth';
      this.cancelArtifactDifferenceScrollFrame();
      this.artifactDifferenceActiveIndex = nextIndex;
      this.$nextTick(() => {
        this.syncArtifactDifferenceSelection();
        const panel = this.$el && this.$el.querySelector
          ? this.$el.querySelector(`#${this.artifactDifferencePanelId}`)
          : null;
        const container = panel && panel.querySelector
          ? panel.querySelector('[data-testid="artifact-diff-content"]')
          : null;
        const target = panel && panel.querySelector
          ? panel.querySelector(`#${this.artifactDifferenceHunkId(nextIndex)}`)
          : null;
        if (!target) return;
        if (shouldFocus && typeof target.focus === 'function') {
          target.focus({ preventScroll: true });
        }
        if (!shouldScroll || !container) return;
        const containerRect = container.getBoundingClientRect();
        const chatViewport = typeof container.closest === 'function'
          ? container.closest('.chat-scroll-area')
          : null;
        const chatViewportRect = chatViewport && typeof chatViewport.getBoundingClientRect === 'function'
          ? chatViewport.getBoundingClientRect()
          : null;
        const visibleTop = chatViewportRect && !isInitialOpen
          ? Math.max(containerRect.top, chatViewportRect.top)
          : containerRect.top;
        const visibleBottom = chatViewportRect && !isInitialOpen
          ? Math.min(containerRect.bottom, chatViewportRect.bottom)
          : containerRect.bottom;
        if (visibleBottom <= visibleTop) return;
        const calculateContainerScrollDelta = (
          viewportTop,
          viewportBottom,
          { alignStart = false } = {},
        ) => {
          if (viewportBottom <= viewportTop) return 0;
          const nextTargetRect = target.getBoundingClientRect();
          const nextTargetStyle = typeof window !== 'undefined'
            && typeof window.getComputedStyle === 'function'
            ? window.getComputedStyle(target)
            : null;
          const nextTargetTop = nextTargetRect.top
            - (Number.parseFloat(nextTargetStyle?.scrollMarginTop) || 0);
          const nextTargetBottom = nextTargetRect.bottom
            + (Number.parseFloat(nextTargetStyle?.scrollMarginBottom) || 0);
          if (alignStart) return nextTargetTop - viewportTop;
          const targetHeight = nextTargetBottom - nextTargetTop;
          const viewportHeight = viewportBottom - viewportTop;
          const startsBefore = nextTargetTop < viewportTop;
          const endsAfter = nextTargetBottom > viewportBottom;
          if (startsBefore && endsAfter) return 0;
          if (startsBefore) {
            return targetHeight > viewportHeight
              ? nextTargetBottom - viewportBottom
              : nextTargetTop - viewportTop;
          }
          if (endsAfter) {
            return targetHeight > viewportHeight
              ? nextTargetTop - viewportTop
              : nextTargetBottom - viewportBottom;
          }
          return 0;
        };
        const applyContainerScroll = (scrollDelta) => {
          if (!scrollDelta) return;
          this.artifactDifferenceProgrammaticScroll = true;
          if (this.artifactDifferenceScrollReleaseTimer !== null) {
            clearTimeout(this.artifactDifferenceScrollReleaseTimer);
          }
          this.artifactDifferenceScrollReleaseTimer = setTimeout(() => {
            this.artifactDifferenceScrollReleaseTimer = null;
            this.artifactDifferenceProgrammaticScroll = false;
          }, scrollBehavior === 'smooth' ? 550 : 0);
          const top = Math.max(0, Number(container.scrollTop || 0) + scrollDelta);
          if (isInitialOpen) {
            container.scrollTop = top;
          } else if (typeof container.scrollBy === 'function') {
            container.scrollBy({ top: scrollDelta, behavior: scrollBehavior });
          } else if (typeof container.scrollTo === 'function') {
            container.scrollTo({ top, left: container.scrollLeft, behavior: scrollBehavior });
          } else {
            container.scrollTop = Math.max(0, Number(container.scrollTop || 0) + scrollDelta);
          }
        };
        applyContainerScroll(calculateContainerScrollDelta(
          visibleTop,
          visibleBottom,
          { alignStart: isInitialOpen },
        ));
        if (!isInitialOpen || !chatViewport) return;

        const settleInitialContainerPosition = () => {
          const settledContainerRect = container.getBoundingClientRect();
          applyContainerScroll(calculateContainerScrollDelta(
            settledContainerRect.top,
            settledContainerRect.bottom,
            { alignStart: true },
          ));
        };

        const revealInChatViewport = () => {
          if (
            !this.artifactDifferenceOpen
            || !target.isConnected
            || !chatViewport.isConnected
          ) return;
          const nextTargetRect = target.getBoundingClientRect();
          const nextChatViewportRect = chatViewport.getBoundingClientRect();
          const nextTargetStyle = typeof window !== 'undefined'
            && typeof window.getComputedStyle === 'function'
            ? window.getComputedStyle(target)
            : null;
          const nextTargetTop = nextTargetRect.top
            - (Number.parseFloat(nextTargetStyle?.scrollMarginTop) || 0);
          const nextTargetBottom = nextTargetRect.bottom
            + (Number.parseFloat(nextTargetStyle?.scrollMarginBottom) || 0);
          const chatViewportHeight = nextChatViewportRect.bottom - nextChatViewportRect.top;
          const navigator = panel && typeof panel.querySelector === 'function'
            ? panel.querySelector('.artifact-difference__navigator')
            : null;
          const navigatorRect = navigator && typeof navigator.getBoundingClientRect === 'function'
            ? navigator.getBoundingClientRect()
            : null;
          const firstChangedSegment = typeof target.querySelector === 'function'
            ? target.querySelector('[data-diff-segment]')
            : null;
          const modificationStartRect = firstChangedSegment
            ? firstChangedSegment.getBoundingClientRect()
            : nextTargetRect;
          const revealTop = navigatorRect ? navigatorRect.top : nextTargetTop;
          const modificationStartRevealHeight = Math.min(
            ARTIFACT_DIFFERENCE_START_REVEAL_PX,
            Math.max(1, modificationStartRect.height),
          );
          const modificationStartBottom = Math.max(
            navigatorRect ? navigatorRect.bottom : nextTargetTop,
            modificationStartRect.top + modificationStartRevealHeight,
          );
          const canRevealWholeHunk = nextTargetBottom - revealTop <= chatViewportHeight;
          const canRevealControlsAndStart = (
            modificationStartBottom - revealTop <= chatViewportHeight
          );
          const revealBottom = canRevealWholeHunk
            ? nextTargetBottom
            : (canRevealControlsAndStart
              ? modificationStartBottom
              : (navigatorRect ? navigatorRect.bottom : nextTargetRect.top + 1));
          const revealHeight = revealBottom - revealTop;
          let chatScrollDelta = 0;
          if (revealHeight <= chatViewportHeight) {
            if (revealTop < nextChatViewportRect.top) {
              chatScrollDelta = revealTop - nextChatViewportRect.top;
            } else if (revealBottom > nextChatViewportRect.bottom) {
              chatScrollDelta = revealBottom - nextChatViewportRect.bottom;
            }
          } else if (
            revealTop < nextChatViewportRect.top
            || revealTop >= nextChatViewportRect.bottom
          ) {
            chatScrollDelta = revealTop - nextChatViewportRect.top;
          }
          if (chatScrollDelta) {
            const nextChatScrollTop = Math.max(
              0,
              Number(chatViewport.scrollTop || 0) + chatScrollDelta,
            );
            if (typeof this.setProgrammaticChatScrollTop === 'function') {
              this.setProgrammaticChatScrollTop(chatViewport, nextChatScrollTop);
            } else {
              chatViewport.scrollTop = nextChatScrollTop;
            }
          }
        };
        const browserWindow = typeof window !== 'undefined' ? window : null;
        if (browserWindow && typeof browserWindow.requestAnimationFrame === 'function') {
          const scheduleInitialFrame = (callback) => {
            this.artifactDifferenceScrollFrame = browserWindow.requestAnimationFrame(() => {
              this.artifactDifferenceScrollFrame = null;
              callback();
            });
          };
          scheduleInitialFrame(() => {
            if (!this.artifactDifferenceOpen || !target.isConnected || !container.isConnected) return;
            settleInitialContainerPosition();
            scheduleInitialFrame(() => {
              if (!this.artifactDifferenceOpen || !target.isConnected || !container.isConnected) return;
              settleInitialContainerPosition();
              revealInChatViewport();
            });
          });
        } else {
          settleInitialContainerPosition();
          revealInChatViewport();
        }
      });
      return nextIndex;
    },
    navigateArtifactDifference(direction) {
      return this.goToArtifactDifference(
        this.artifactDifferenceActiveIndex + Number(direction || 0),
        { behavior: 'smooth' },
      );
    },
    emitArtifactProjectionState(state, requestKey = '') {
      if (!this.isArtifactCard) return;
      this.$emit('artifact-state', {
        state: String(state || 'loading'),
        requestKey: String(requestKey || ''),
        chatUuid: String(this.chatUuid || ''),
        receipt: this.receipt,
      });
    },
    resetArtifactProjection() {
      this.disposeArtifactHelpPopover();
      this.resetArtifactDifferenceState({ clearCache: true });
      this.artifactProjection = null;
      this.artifactVersions = [];
      this.artifactRequestKey = '';
      const resetGeneration = ++this.artifactRequestGeneration;
      this.artifactRetryVersion = null;
      this.artifactLoadError = false;
      this.artifactLoadFailure = null;
      this.artifactLoadRetrying = false;
      this.emitArtifactProjectionState('loading');
      this.$nextTick(() => {
        if (this.artifactRequestGeneration !== resetGeneration) return;
        this.loadArtifactVersion(this.receiptArtifactVersion);
      });
    },
    async loadArtifactVersion(version = null, { refreshVersions = true } = {}) {
      if (!this.isArtifactCard || !this.chatUuid || !this.artifactId) return;
      const requestedVersion = Number(version);
      const pinnedVersion = Number.isInteger(requestedVersion) && requestedVersion > 0
        ? requestedVersion
        : null;
      if (
        this.artifactDifferenceOpen
        && pinnedVersion
        && pinnedVersion !== Number(this.selectedArtifactVersion)
      ) {
        this.closeArtifactDifferences({ restoreFocus: false, clearResult: true });
      }
      const key = `${this.chatUuid}:${this.artifactId}:${pinnedVersion || 'current'}`;
      if (this.artifactRequestKey === key && this.artifactProjection
        && this.artifactProjection.available !== false && !this.artifactLoadError) return;
      const requestGeneration = ++this.artifactRequestGeneration;
      const requestIsCurrent = () => (
        this.artifactRequestKey === key
        && this.artifactRequestGeneration === requestGeneration
      );
      const retainsVisibleProjection = Boolean(this.artifactProjection && this.renderedArtifact);
      const keepsVisibleAccessNotice = retainsVisibleProjection && this.artifactLoadError;
      this.artifactRequestKey = key;
      this.artifactRetryVersion = pinnedVersion;
      this.artifactLoadError = keepsVisibleAccessNotice;
      this.artifactLoadRetrying = keepsVisibleAccessNotice;
      if (!keepsVisibleAccessNotice) this.artifactLoadFailure = null;
      // Version navigation updates this card in place. Keep the compatibility
      // document suppressed while the next immutable projection is fetched;
      // remounting both cards for this interval causes a visible width flash.
      this.emitArtifactProjectionState(retainsVisibleProjection ? 'ready' : 'loading', key);
      const query = new URLSearchParams({ chat_uuid: this.chatUuid });
      if (pinnedVersion) query.set('version', String(pinnedVersion));
      try {
        const projectionRequest = fetch(
          `/chat/document-artifacts/${encodeURIComponent(this.artifactId)}?${query.toString()}`,
        );
        const projectionResponse = await projectionRequest;
        const projection = await projectionResponse.json().catch(() => ({}));
        const denied = projectionResponse.status === 403 && projection.available === false;
        if ((!projectionResponse.ok && !denied) || !requestIsCurrent()) {
          if (!requestIsCurrent()) return;
          throw createToolResultAccessError(toolResultAccessFeedback({
            response: projectionResponse,
            resultType: 'document',
            retryable: projection.retryable === true,
          }));
        }
        this.disposeArtifactHelpPopover();
        this.artifactProjection = projection;
        this.$nextTick(() => {
          if (requestIsCurrent()) this.initializeArtifactHelpPopover();
        });
        if (projection.available === false) {
          this.artifactLoadError = false;
          this.artifactLoadRetrying = false;
          this.emitArtifactProjectionState('unavailable', key);
        }
        const versionsRequest = refreshVersions
          ? fetch(`/chat/document-artifacts/${encodeURIComponent(this.artifactId)}/versions?chat_uuid=${encodeURIComponent(this.chatUuid)}`)
            .catch(() => null)
          : null;
        if (versionsRequest) {
          try {
            const versionsResponse = await versionsRequest;
            const versionsPayload = versionsResponse
              ? await versionsResponse.json().catch(() => ({}))
              : {};
            if (versionsResponse && versionsResponse.ok && requestIsCurrent()) {
              this.artifactVersions = Array.isArray(versionsPayload.versions)
                ? versionsPayload.versions.filter(item => item && Number(item.version_number) > 0)
                : [];
            }
          } catch (error) { }
        }
        if (projection.available === false || !requestIsCurrent()) return;
        this.$nextTick(() => {
          if (!requestIsCurrent()) return;
          const renderedElement = this.$el && this.$el.querySelector
            ? this.$el.querySelector('[data-testid="artifact-inline-document"]')
            : null;
          if (!this.renderedArtifact || !renderedElement) {
            this.artifactLoadError = true;
            this.artifactLoadRetrying = false;
            this.artifactLoadFailure = toolResultAccessFeedback({
              resultType: 'document',
              retryable: true,
            });
            this.emitArtifactProjectionState('error', key);
            return;
          }
          this.artifactLoadError = false;
          this.artifactLoadFailure = null;
          this.artifactLoadRetrying = false;
          this.attachArtifactLinks();
          this.initializeArtifactHelpPopover();
          this.emitArtifactProjectionState('ready', key);
          this.$emit('artifact-ready', this.receipt);
        });
      } catch (error) {
        if (requestIsCurrent()) {
          this.artifactLoadError = true;
          this.artifactLoadRetrying = false;
          this.artifactLoadFailure = toolResultAccessFeedback({
            error,
            resultType: 'document',
          });
          this.emitArtifactProjectionState(
            this.artifactProjection && this.renderedArtifact ? 'ready' : 'error',
            key,
          );
        }
      }
    },
    retryArtifactLoad() {
      if (this.artifactLoadRetrying) return;
      return this.loadArtifactVersion(this.artifactRetryVersion, { refreshVersions: true });
    },
    attachArtifactLinks() {
      try {
        if (typeof attachDocDetailsToLinks === 'function') {
          attachDocDetailsToLinks(`#${this.artifactCardId}`);
        }
      } catch (error) { }
    },
    emitAction(action, options = {}) {
      this.$emit('action', {
        action: String(action || ''),
        receipt: this.receipt,
        artifactVersion: options.artifactVersion || this.selectedArtifactVersion || null,
      });
    },
    emitReviewAction(action) {
      if (!action || typeof action !== 'object' || !String(action.id || '').trim()) return;
      this.$emit('reviewAction', {
        ...action,
        reviewId: String(action.reviewId || this.receiptReviewId || '').trim(),
        receipt: this.receipt,
      });
    },
    reviewActionClass(action, index) {
      const isPrimary = Number(index) === 0;
      return {
        'tool-receipt__action--primary': isPrimary,
        'tool-receipt__action--secondary': !isPrimary,
      };
    },
    displayAction(action) {
      const normalized = normalizeActionLabel(action);
      return ACTION_LABELS[normalized] || String(action || '');
    },
    actionClass(action) {
      const normalized = normalizeActionLabel(action);
      const primary = [
        'seleccionar documentos',
        'seleccionar otros documentos',
        'ver revision',
        'abrir revision',
        'reintentar respuesta',
        'abrir',
        'abrir pasajes',
        'confirmar reemplazo',
      ].includes(normalized);
      const danger = ['cancelar', 'cancelar solicitud', 'cancelar reemplazo', 'detener'].includes(normalized);
      const secondary = ['omitir', 'continuar sin documentos'].includes(normalized);
      return {
        'tool-receipt__action--primary': primary,
        'tool-receipt__action--secondary': secondary,
        'tool-receipt__action--danger': danger,
      };
    },
    contextTypeLabel(type) {
      return ({
        documento: 'Documento',
        imagen: 'Imagen',
        audio: 'Audio',
        video: 'Video',
      })[String(type || '').toLocaleLowerCase('es-ES')] || String(type || 'Documento');
    },
  },
  template: `
    <section
      class="tool-receipt"
      :class="['tool-receipt--' + status, 'tool-receipt--' + toolKind, {
        'tool-receipt--artifact-reply': isArtifactReply,
        'tool-receipt--orphan-artifact': isOrphanedArtifact,
        'tool-receipt--artifact-inline': isArtifactCard,
      }]"
      :data-tour="isReview ? 'chat-response-review' : null"
      :data-tool-run-id="receipt.tool_run_id"
      :aria-busy="isBusy ? 'true' : 'false'"
      :aria-labelledby="isArtifactCard ? null : receiptTitleId"
      @lexius-tour-set-document-edition-comparison="setArtifactDifferenceTourState"
      role="group"
    >
      <template v-if="isArtifactCard">
        <div
          v-if="artifactProjection && renderedArtifact"
          :id="artifactCardId"
          class="lexiusdoc-card dynamic-color tool-receipt__artifact-document"
          data-testid="artifact-inline-document"
          :data-tour="hasArtifactDifferenceTour ? 'document-edition-workspace' : null"
          :data-document-edition-selected-version="selectedArtifactVersion"
          :data-document-edition-comparison-state="artifactDifferenceTourState"
          :data-document-edition-has-context="artifactDifferenceTourHasContext ? 'true' : 'false'"
        >
          <div
            class="lexiusdoc-card__header"
            :data-tour="hasArtifactDifferenceTour ? 'document-edition-overview' : null"
          >
            <div class="lexiusdoc-card__info">
              <div class="lexiusdoc-card__icon"><i class="anticon anticon-file-text"></i></div>
              <div class="lexiusdoc-card__meta">
                <span class="lexiusdoc-card__label">{{ versionRetained ? title : isOrphanedArtifact ? 'Documento recuperado' : 'Documento generado' }}</span>
                <h4 class="lexiusdoc-card__title">{{ artifactTitle }}</h4>
                <p class="lexiusdoc-card__hint">
                  Versión {{ selectedArtifactVersion }}<span v-if="visibleArtifactWordCount !== null"> · {{ visibleArtifactWordCount }} palabras</span><span v-if="versionRetained"> · {{ summary }}</span><span v-else-if="artifactProjection.change_summary"> · {{ artifactProjection.change_summary }}</span>
                </p>
              </div>
            </div>
            <div class="lexiusdoc-card__actions">
              <button
                type="button"
                class="lexiusdoc-card__action-btn lexiusdoc-card__action-btn--copy"
                :class="artifactActionButtonClass('copy')"
                data-testid="artifact-reply-copy"
                :aria-label="artifactActionLabel('copy', 'Copiar documento')"
                :title="artifactActionLabel('copy', 'Copiar documento')"
                :disabled="artifactActionFeedback.phase === 'pending'"
                @click="emitArtifactAction('Copiar', 'copy')"
              ><i :class="artifactActionIcon('copy', 'anticon anticon-copy')"></i></button>
              <button
                type="button"
                class="lexiusdoc-card__action-btn lexiusdoc-card__action-btn--pdf"
                :class="artifactActionButtonClass('pdf')"
                data-testid="artifact-reply-pdf"
                :aria-label="artifactActionLabel('pdf', 'Descargar PDF')"
                :title="artifactActionLabel('pdf', 'Descargar PDF')"
                :disabled="artifactActionFeedback.phase === 'pending'"
                @click="emitArtifactAction('artifact-export-pdf', 'pdf')"
              ><i :class="artifactActionIcon('pdf', 'anticon anticon-file-pdf')"></i></button>
              <button
                type="button"
                class="lexiusdoc-card__action-btn lexiusdoc-card__action-btn--docx"
                :class="artifactActionButtonClass('docx')"
                data-testid="artifact-reply-docx"
                :aria-label="artifactActionLabel('docx', 'Descargar Word')"
                :title="artifactActionLabel('docx', 'Descargar Word')"
                :disabled="artifactActionFeedback.phase === 'pending'"
                @click="emitArtifactAction('artifact-export-docx', 'docx')"
              ><i :class="artifactActionIcon('docx', 'anticon anticon-file-word')"></i></button>
              <span
                v-if="artifactActionFeedback.phase !== 'idle'"
                class="lexiusdoc-card__action-feedback"
                :class="'lexiusdoc-card__action-feedback--' + artifactActionFeedback.phase"
                data-testid="artifact-action-feedback"
                role="status"
                aria-live="polite"
              >{{ artifactActionCopy(artifactActionFeedback.key) }}</span>
            </div>
          </div>
          <div
            v-if="artifactLoadError"
            class="tool-receipt__artifact-state tool-receipt__artifact-state--access tool-receipt__artifact-state--retained"
            :class="artifactLoadFailure ? 'tool-receipt__artifact-state--' + artifactLoadFailure.kind : ''"
            data-testid="artifact-result-access-error"
            role="alert"
          >
            <span class="tool-receipt__artifact-state-icon" aria-hidden="true"><i :class="artifactLoadFailure ? artifactLoadFailure.iconClass : 'anticon anticon-warning'"></i></span>
            <span class="tool-receipt__artifact-state-copy">
              <strong>{{ artifactLoadFailure ? artifactLoadFailure.title : 'No pudimos abrir el documento' }}</strong>
              <span>{{ artifactLoadFailure ? artifactLoadFailure.message : 'Vuelve a intentarlo desde esta conversación.' }}</span>
              <small>{{ artifactLoadFailure ? (artifactLoadFailure.reassurance + ' La versión que ya estaba abierta permanece visible.') : 'La versión que ya estaba abierta permanece visible.' }}</small>
            </span>
            <button
              type="button"
              data-testid="artifact-result-access-retry"
              :disabled="artifactLoadRetrying"
              :aria-busy="artifactLoadRetrying ? 'true' : 'false'"
              @click="retryArtifactLoad"
            >
              <i v-if="artifactLoadRetrying" class="anticon anticon-loading anticon-spin" aria-hidden="true"></i>
              <span>{{ artifactLoadRetrying ? 'Reintentando…' : (artifactLoadFailure ? artifactLoadFailure.actionLabel : 'Reintentar') }}</span>
            </button>
          </div>
          <div
            v-if="hasArtifactDifferenceComparison"
            class="artifact-difference__entry"
            :class="{ 'is-open': artifactDifferenceOpen, 'is-unavailable': artifactParentUnavailable }"
            data-tour="document-edition-comparison"
          >
            <span class="artifact-difference__entry-icon" aria-hidden="true">
              <i class="anticon anticon-diff"></i>
            </span>
            <div class="artifact-difference__entry-copy">
              <div class="artifact-difference__entry-heading">
                <strong>Revisión de cambios</strong>
                <button
                  ref="artifactHelpButton"
                  type="button"
                  class="artifact-difference__help"
                  data-testid="artifact-edition-help"
                  data-toggle="popover"
                  data-trigger="click"
                  data-placement="bottom"
                  data-boundary="viewport"
                  title="Edita sin perder tus versiones anteriores"
                  data-content="Pide el cambio en este mismo chat e indica qué quieres modificar y qué debe mantenerse. LEXIUS guarda el resultado como una nueva versión y conserva las anteriores. Cuando Ver diferencias esté disponible, úsalo para comprobar cada ajuste antes de seguir trabajando o descargar el documento."
                  aria-label="Ayuda para editar y comparar versiones del documento"
                ><i class="anticon anticon-question" aria-hidden="true"></i></button>
              </div>
              <span v-if="artifactParentUnavailable">La versión anterior no está disponible.</span>
              <span v-else>Versión {{ selectedArtifactVersion }} frente a versión {{ artifactParentVersion }}</span>
            </div>
            <button
              type="button"
              class="artifact-difference__entry-button"
              data-testid="artifact-diff-open"
              :aria-expanded="artifactDifferenceOpen ? 'true' : 'false'"
              :aria-controls="artifactDifferencePanelId"
              :disabled="artifactParentUnavailable"
              @click="toggleArtifactDifferences"
            >
              <i :class="['anticon', artifactDifferenceLoading ? 'anticon-loading anticon-spin' : (artifactDifferenceOpen ? 'anticon-arrow-left' : 'anticon-diff')]" aria-hidden="true"></i>
              <span>{{ artifactDifferenceOpen ? 'Volver al documento' : 'Ver diferencias' }}</span>
            </button>
          </div>
          <nav
            v-if="artifactVersions.length > 1"
            class="tool-workspace__versions tool-receipt__artifact-versions"
            data-tour="document-edition-versions"
            aria-label="Historial de versiones"
          >
            <button
              v-for="version in artifactVersions"
              :key="version.version_number"
              type="button"
              :aria-current="Number(selectedArtifactVersion) === Number(version.version_number) ? 'true' : null"
              :aria-label="version.available === false ? ('Versión ' + version.version_number + ' no disponible') : ('Abrir versión ' + version.version_number)"
              :title="version.available === false ? 'Procedencia no disponible' : (version.change_summary || ('Versión ' + version.version_number))"
              :disabled="version.available === false"
              :data-document-edition-tour-version="Number(version.version_number) === Number(artifactDifferenceTourVersion) ? 'true' : null"
              :data-document-edition-version="version.version_number"
              @click="loadArtifactVersion(version.version_number, { refreshVersions: false })"
            >v{{ version.version_number }}</button>
          </nav>
          <ul v-if="warnings.length" class="tool-receipt__warnings" data-testid="artifact-source-warnings" role="status">
            <li v-for="(warning, index) in warnings" :key="index">{{ warning }}</li>
          </ul>
          <section
            v-if="artifactDifferenceOpen"
            :id="artifactDifferencePanelId"
            class="artifact-difference"
            data-testid="artifact-diff-panel"
            role="region"
            :aria-labelledby="artifactDifferenceTitleId"
            tabindex="-1"
            @keydown.esc.stop.prevent="closeArtifactDifferences()"
            @keydown.left.stop.prevent="navigateArtifactDifference(-1)"
            @keydown.right.stop.prevent="navigateArtifactDifference(1)"
          >
            <header class="artifact-difference__header" data-tour="document-edition-comparison-header">
              <div>
                <span class="artifact-difference__eyebrow">Control de cambios</span>
                <h5 :id="artifactDifferenceTitleId">De la versión {{ artifactParentVersion }} a la {{ selectedArtifactVersion }}</h5>
                <p>Lee el documento completo en su orden original. Cada edición muestra el texto anterior junto al nuevo en el lugar donde se hizo.</p>
              </div>
              <button
                type="button"
                class="artifact-difference__close"
                aria-label="Volver al documento"
                title="Volver al documento"
                @click="closeArtifactDifferences()"
              >
                <i class="anticon anticon-arrow-left" aria-hidden="true"></i>
                <span>Volver al documento</span>
              </button>
            </header>

            <div v-if="artifactDifferenceLoading" class="artifact-difference__state" role="status" aria-live="polite">
              <span class="artifact-difference__loader" aria-hidden="true"><i class="anticon anticon-loading anticon-spin"></i></span>
              <div><strong>Comparando ambas versiones</strong><span>Identificando cambios de palabras, cifras y puntuación…</span></div>
            </div>
            <div v-else-if="artifactDifferenceError" class="artifact-difference__state artifact-difference__state--error" role="alert">
              <span class="artifact-difference__state-icon" aria-hidden="true"><i class="anticon anticon-warning"></i></span>
              <div><strong>No se pudo abrir la comparación</strong><span>{{ artifactDifferenceError }}</span></div>
              <button type="button" @click="retryArtifactDifferences">Reintentar</button>
            </div>
            <template v-else-if="artifactDifferenceModel">
              <div
                class="artifact-difference__summary"
                data-testid="artifact-diff-summary"
                data-tour="document-edition-summary"
              >
                <div class="artifact-difference__legend" aria-label="Leyenda de cambios">
                  <span class="is-added"><i aria-hidden="true"></i>Texto añadido</span>
                  <span class="is-removed"><i aria-hidden="true"></i>Texto eliminado</span>
                </div>
                <dl aria-label="Resumen de cambios">
                  <div><dt>Cambios</dt><dd>{{ artifactDifferenceHunks.length }}</dd></div>
                  <div class="is-added"><dt>Adiciones</dt><dd>+{{ artifactDifferenceCounts.additions || 0 }}</dd></div>
                  <div class="is-removed"><dt>Eliminaciones</dt><dd>−{{ artifactDifferenceCounts.removals || 0 }}</dd></div>
                </dl>
              </div>
              <p v-if="artifactDifferenceHasWarning" class="artifact-difference__warning" role="status">
                La revisión contiene cambios muy amplios; algunos pasajes se muestran como bloques completos.
              </p>
              <div
                v-if="artifactDifferenceHunks.length"
                class="artifact-difference__navigator"
                data-tour="document-edition-navigator"
              >
                <p data-testid="artifact-diff-position" aria-live="polite" aria-atomic="true">
                  Cambio <strong>{{ artifactDifferenceActiveIndex + 1 }}</strong> de {{ artifactDifferenceHunks.length }}
                </p>
                <div class="artifact-difference__navigator-controls">
                  <button
                    type="button"
                    class="artifact-difference__navigator-button"
                    data-testid="artifact-diff-first"
                    title="Primer cambio"
                    :disabled="artifactDifferenceActiveIndex === 0"
                    @click="goToArtifactDifference(0, { behavior: 'auto' })"
                  >
                    <i class="anticon anticon-double-left" aria-hidden="true"></i>
                    <span>Primero</span>
                  </button>
                  <button
                    type="button"
                    class="artifact-difference__navigator-button"
                    data-testid="artifact-diff-prev"
                    title="Cambio anterior"
                    :disabled="artifactDifferenceActiveIndex === 0"
                    @click="navigateArtifactDifference(-1)"
                  >
                    <i class="anticon anticon-arrow-left" aria-hidden="true"></i>
                    <span>Anterior</span>
                  </button>
                  <button
                    type="button"
                    class="artifact-difference__navigator-button artifact-difference__navigator-button--next"
                    data-testid="artifact-diff-next"
                    title="Cambio siguiente"
                    :disabled="artifactDifferenceActiveIndex >= artifactDifferenceHunks.length - 1"
                    @click="navigateArtifactDifference(1)"
                  >
                    <span>Siguiente</span>
                    <i class="anticon anticon-arrow-right" aria-hidden="true"></i>
                  </button>
                  <button
                    type="button"
                    class="artifact-difference__navigator-button"
                    data-testid="artifact-diff-last"
                    title="Último cambio"
                    :disabled="artifactDifferenceActiveIndex >= artifactDifferenceHunks.length - 1"
                    @click="goToArtifactDifference(artifactDifferenceHunks.length - 1, { behavior: 'auto' })"
                  >
                    <span>Último</span>
                    <i class="anticon anticon-double-right" aria-hidden="true"></i>
                  </button>
                </div>
                <span class="artifact-difference__keyboard-hint">También puedes usar ← y →</span>
              </div>
              <div
                v-if="!artifactDifferenceHunks.length"
                class="artifact-difference__empty"
                data-testid="artifact-diff-empty"
                data-tour="document-edition-empty"
                role="status"
              >
                <i class="anticon anticon-check-circle" aria-hidden="true"></i>
                <strong>No hay cambios de texto</strong>
                <span>El contenido de ambas versiones coincide.</span>
              </div>
              <article
                v-if="artifactDifferenceDocumentHtml"
                class="artifact-difference__document"
                data-testid="artifact-diff-content"
                data-tour="document-edition-content"
                aria-label="Documento completo con control de cambios"
                v-html="artifactDifferenceDocumentHtml"
                @click="selectArtifactDifferenceChange"
                @pointerdown.passive="handleArtifactDifferenceManualScrollIntent"
                @wheel.passive="handleArtifactDifferenceManualScrollIntent"
                @keydown="handleArtifactDifferenceManualScrollKey"
                @scroll.passive="handleArtifactDifferenceScroll"
              ></article>
            </template>
          </section>
          <div v-show="!artifactDifferenceOpen" class="lexiusdoc-card__body-frame">
            <article :id="artifactCardId + '-content'" class="lexiusdoc-card__content" v-html="renderedArtifact"></article>
          </div>
        </div>
        <div
          v-else-if="artifactProvenanceUnavailable"
          class="tool-receipt__artifact-state"
          data-testid="artifact-provenance-unavailable"
          role="status"
        >
          <strong>No pudimos abrir esta versión.</strong>
          <span>{{ artifactProjection.message || 'No pudimos comprobar el acceso a una fuente del documento.' }}</span>
          <span>El documento sigue guardado. Puedes pedir al asistente que lo recupere.</span>
          <button type="button" data-testid="artifact-provenance-retry" :disabled="artifactLoadRetrying" @click="retryArtifactLoad">Reintentar</button>
          <nav v-if="artifactVersions.length" class="tool-workspace__versions tool-receipt__artifact-versions" aria-label="Historial de versiones">
            <button
              v-for="version in artifactVersions"
              :key="version.version_number"
              type="button"
              :aria-current="Number(selectedArtifactVersion) === Number(version.version_number) ? 'true' : null"
              :aria-label="version.available === false ? ('Versión ' + version.version_number + ' no disponible') : ('Abrir versión ' + version.version_number)"
              :title="version.available === false ? 'Procedencia no disponible' : (version.change_summary || ('Versión ' + version.version_number))"
              :disabled="version.available === false"
              @click="loadArtifactVersion(version.version_number, { refreshVersions: false })"
            >v{{ version.version_number }}</button>
          </nav>
        </div>
        <div
          v-else-if="artifactLoadError"
          class="tool-receipt__artifact-state tool-receipt__artifact-state--access"
          :class="artifactLoadFailure ? 'tool-receipt__artifact-state--' + artifactLoadFailure.kind : ''"
          data-testid="artifact-result-access-error"
          role="alert"
        >
          <span class="tool-receipt__artifact-state-icon" aria-hidden="true"><i :class="artifactLoadFailure ? artifactLoadFailure.iconClass : 'anticon anticon-warning'"></i></span>
          <span class="tool-receipt__artifact-state-copy">
            <strong>{{ artifactLoadFailure ? artifactLoadFailure.title : 'No pudimos abrir el documento' }}</strong>
            <span>{{ artifactLoadFailure ? artifactLoadFailure.message : 'Vuelve a intentarlo desde esta conversación.' }}</span>
            <small>{{ artifactLoadFailure ? artifactLoadFailure.reassurance : 'La copia incluida en la respuesta sigue disponible.' }}</small>
          </span>
          <button
            type="button"
            data-testid="artifact-result-access-retry"
            :disabled="artifactLoadRetrying"
            :aria-busy="artifactLoadRetrying ? 'true' : 'false'"
            @click="retryArtifactLoad"
          >
            <i v-if="artifactLoadRetrying" class="anticon anticon-loading anticon-spin" aria-hidden="true"></i>
            <span>{{ artifactLoadRetrying ? 'Reintentando…' : (artifactLoadFailure ? artifactLoadFailure.actionLabel : 'Reintentar') }}</span>
          </button>
        </div>
        <p v-else class="tool-receipt__artifact-state" role="status">Cargando documento…</p>
      </template>
      <template v-else>
        <div class="tool-receipt__rail" aria-hidden="true"></div>
        <div class="tool-receipt__body">
          <div class="tool-receipt__header">
            <div class="tool-receipt__identity">
              <span class="tool-receipt__tool-icon" aria-hidden="true"><i :class="toolIconClass"></i></span>
              <div class="tool-receipt__heading">
                <span class="tool-receipt__eyebrow">{{ eyebrow }}</span>
                <h3 :id="receiptTitleId">{{ title }}</h3>
              </div>
            </div>
            <span class="tool-receipt__status" role="status" aria-live="polite">
              <span class="tool-receipt__status-dot" aria-hidden="true"></span>
              {{ statusLabel }}
            </span>
          </div>
          <p v-if="summary" class="tool-receipt__summary">{{ summary }}</p>
          <p
            v-if="reviewCardExplanation"
            class="tool-receipt__summary tool-receipt__review-next-step"
            data-testid="review-card-next-step"
          >{{ reviewCardExplanation }}</p>
          <div v-if="isContextRequest && requestedDocuments.length" class="tool-receipt__requested-documents">
            <p>{{ requestedDocumentsHeading }}</p>
            <ul>
              <li v-for="(request, requestIndex) in requestedDocuments" :key="request.requestItemId || (request.label + '-' + requestIndex)">
                <div class="tool-receipt__requested-heading">
                  <strong>{{ request.label }}</strong>
                  <span :class="['tool-receipt__priority', 'tool-receipt__priority--' + request.priority]">
                    {{ request.priority === 'necesario' ? 'Necesario' : 'Recomendado' }}
                  </span>
                </div>
                <span v-if="request.detail">{{ request.detail }}</span>
                <span class="tool-receipt__accepted-types">
                  Formatos: {{ request.acceptedTypes.map(contextTypeLabel).join(', ') }}
                </span>
              </li>
            </ul>
          </div>
          <dl v-if="isReview && reviewStats.length" class="tool-receipt__review-stats" aria-label="Resumen de la revisión">
            <div v-for="stat in reviewStats" :key="stat.label">
              <dt>{{ stat.label }}</dt>
              <dd>{{ stat.value }}</dd>
            </div>
          </dl>
          <dl v-if="isSourceRead && sourceStats.length" class="tool-receipt__source-stats" aria-label="Resumen de las fuentes consultadas">
            <div v-for="stat in sourceStats" :key="stat.label" :class="stat.tone ? 'is-' + stat.tone : null">
              <dd>{{ stat.value }}</dd>
              <dt>{{ stat.label }}</dt>
            </div>
          </dl>
          <ul v-if="warnings.length" class="tool-receipt__warnings">
            <li v-for="(warning, index) in warnings" :key="index">{{ warning }}</li>
          </ul>
          <div
            v-if="isActiveReview && reviewCardActions.length"
            class="tool-receipt__actions"
            data-testid="review-card-actions"
          >
            <button
              v-for="(action, actionIndex) in reviewCardActions"
              :key="action.id + '-' + action.reviewId"
              type="button"
              class="tool-receipt__action"
              :class="reviewActionClass(action, actionIndex)"
              @click="emitReviewAction(action)"
            >{{ action.label }}</button>
          </div>
          <div v-else-if="actions.length" class="tool-receipt__actions">
            <button
              v-for="action in actions"
              :key="action"
              type="button"
              class="tool-receipt__action"
              :class="actionClass(action)"
              @click="emitAction(action)"
            >{{ displayAction(action) }}</button>
          </div>
        </div>
      </template>
    </section>
  `,
};
